import { CSSProperties } from 'react';
import merge from 'lodash/merge';
import styled, { css, CSSObject } from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { breakpoint, BreakpointProp } from '../../utils/styles/breakpoints';
import { Box, BoxProps } from '../layout/Box';

export type TypographyVariant =
    | 'title1'
    | 'title2'
    | 'title3'
    | 'body'
    | 'body2'
    | 'caption'
    | 'caption2';

type TypographyWeight = 'regular' | 'medium' | 'bold' | 'heavy';

type TypographyColor = 'primary' | 'invert' | 'secondary' | 'link' | 'error' | 'inherit';

export interface TypographyProps extends BoxProps {
    // увеличенный line-height
    loose?: boolean;
    noWrap?: boolean;
    lines?: number;
    color?: TypographyColor | CSSProperties['color'];
    weight?: TypographyWeight;
    variant?: TypographyVariant;
    textShadow?: BreakpointProp<CSSProperties['textShadow']>;
    textOverflow?: BreakpointProp<CSSProperties['textOverflow']>;
    flexShrink?: BreakpointProp<CSSProperties['flexShrink']>;
}

const VARIANT_MAPPING: Record<TypographyVariant, CSSObject> = {
    title1: {
        fontSize: '34px',
        lineHeight: '34px',
        '--line-height': '34px',
    },

    title2: {
        fontSize: '24px',
        lineHeight: '26px',
        '--line-height': '26px',
    },

    title3: {
        fontSize: '20px',
        lineHeight: '24px',
        '--line-height': '24px',
    },

    body: {
        fontSize: '16px',
        lineHeight: '20px',
        '--line-height': '20px',
    },

    body2: {
        fontSize: '14px',
        lineHeight: '18px',
        '--line-height': '18px',
    },

    caption: {
        fontSize: '12px',
        lineHeight: '14px',
        '--line-height': '14px',
    },
    caption2: {
        fontSize: '10px',
        lineHeight: '12px',
        '--line-height': '12px',
    },
};

const VARIANT_LOOSE_MAPPING: Record<TypographyVariant, CSSObject> = {
    title1: {
        ...VARIANT_MAPPING.title1,
        lineHeight: '38px',
        '--line-height': '38px',
    },

    title2: {
        ...VARIANT_MAPPING.title2,
        lineHeight: '30px',
        '--line-height': '30px',
    },

    title3: {
        ...VARIANT_MAPPING.title3,
        lineHeight: '26px',
        '--line-height': '26px',
    },

    body: {
        ...VARIANT_MAPPING.body,
        lineHeight: '22px',
        '--line-height': '22px',
    },

    body2: {
        ...VARIANT_MAPPING.body2,
    },

    caption: {
        ...VARIANT_MAPPING.caption,
        lineHeight: '16px',
        '--line-height': '16px',
    },
    caption2: {
        ...VARIANT_MAPPING.caption2,
    },
};

const WEIGHT_MAPPING: Record<TypographyWeight, CSSObject> = {
    regular: { fontWeight: '400' },
    medium: { fontWeight: '500' },
    bold: { fontWeight: '700' },
    heavy: { fontWeight: '800' },
};

const COLOR_MAPPING: Record<TypographyColor, CSSObject> = {
    primary: { color: 'var(--color-primary)' },
    invert: { color: 'var(--color-invert)' },
    secondary: { color: 'var(--color-secondary)' },
    error: { color: 'var(--color-danger)' },
    link: { color: 'var(--color-link)' },
    inherit: { color: 'inherit' },
};

const typographyModelStyles = (props: TypographyProps): CSSObject =>
    merge(
        props.loose
            ? VARIANT_LOOSE_MAPPING[props.variant ?? 'body']
            : VARIANT_MAPPING[props.variant ?? 'body'],

        WEIGHT_MAPPING[props.weight ?? 'regular'],

        props.color && COLOR_MAPPING.hasOwnProperty(props.color)
            ? // @ts-ignore
              COLOR_MAPPING[props.color]
            : { color: props.color },

        breakpoint('flexShrink', props.flexShrink),
        breakpoint('textOverflow', props.textOverflow),
    );

export const Typography = styled(Box).withConfig({
    displayName: 'Typography',
    shouldForwardProp: shouldIgnoreProp(
        'weight',
        'loose',
        'color',
        'noWrap',
        'variant',
        'textShadow',
        'flexShrink',
        'textOverflow',
    ),
})<TypographyProps>`
    font-style: normal;

    ${typographyModelStyles}

    ${(props: TypographyProps) =>
        props.noWrap &&
        css`
            white-space: nowrap;
        `}

    ${(props: TypographyProps) =>
        props.lines &&
        props.lines >= 1 &&
        css`
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            -webkit-line-clamp: ${props.lines};
        `}
    ${(props: TypographyProps) =>
        props.wordBreak &&
        css`
            word-break: ${props.wordBreak};
        `}

    ${(props: TypographyProps) =>
        props.letterSpacing &&
        css`
            letter-spacing: ${props.letterSpacing};
        `}
`;

Typography.defaultProps = {
    as: 'p',
    m: 0,
    display: 'block',
    variant: 'body',
    weight: 'regular',
    color: 'primary',
    textDecoration: 'none',
};
